import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "monthLabel",
    "calendarGrid",
    "dayHeaders",
    "selectedDateLabel",
    "timeSlotsContainer",
    "scheduleButton",
    "mobileScheduleButton",
    "scheduleForm",
    "selectedDateInput",
    "selectedTimeInput",
    "selectedTimeZoneInput",
    "timeSlotRadio",
    "timezoneSelect"
  ]

  static values = {
    year: Number,
    month: Number,
    selectedDate: String,
    availableSlots: Array,
    availableSlotStarts: Array,
    takenSlotStarts: Array,
    timeZoneMappings: Object,
    scheduleUrl: String,
    refreshUrl: String,
    hasSlotsWithinSixtyDays: Boolean,
    noSlotsText: String,
    takenSlotText: String
  }

  connect() {
    if (this.hasTimeSlotsContainerTarget) {
      this.selectDatePromptText = this.timeSlotsContainerTarget.textContent.trim()
    }

    const now = new Date()
    this.yearValue = now.getFullYear()
    this.monthValue = now.getMonth()
    this.availabilityRequestId = 0
    this.renderDayHeaders()
    this.renderCalendar()
  }

  renderDayHeaders() {
    if (!this.hasDayHeadersTarget) return

    const formatter = new Intl.DateTimeFormat(this.#uiLocale(), {
      weekday: "narrow",
      timeZone: "UTC"
    })

    // Sunday-first order to match the grid math based on Date#getDay().
    const sunday = new Date(Date.UTC(2021, 7, 1))
    const headerHtml = Array.from({ length: 7 }, (_, offset) => {
      const label = formatter.format(new Date(sunday.getTime() + (offset * 24 * 60 * 60 * 1000)))
      return `<div class="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-text-text-sm tw-font-medium tw-text-blue-gray-800">${label}</div>`
    }).join("")

    this.dayHeadersTarget.innerHTML = headerHtml
  }

  async timeZoneChanged() {
    await this.#reloadAvailabilityForVisibleMonth()
    this.renderCalendar()
    if (this.selectedDateValue) {
      this.renderTimeSlots()
    } else {
      this.#disableScheduleButtons()
    }
  }

  async previousMonth() {
    this.selectedDateValue = ""
    this.#disableScheduleButtons()
    if (this.monthValue === 0) {
      this.monthValue = 11
      this.yearValue -= 1
    } else {
      this.monthValue -= 1
    }
    await this.#reloadAvailabilityForVisibleMonth()
    this.renderCalendar()
  }

  async nextMonth() {
    this.selectedDateValue = ""
    this.#disableScheduleButtons()
    if (this.monthValue === 11) {
      this.monthValue = 0
      this.yearValue += 1
    } else {
      this.monthValue += 1
    }
    await this.#reloadAvailabilityForVisibleMonth()
    this.renderCalendar()
  }

  selectDate(event) {
    const date = event.currentTarget.dataset.date
    if (!date) return

    this.selectedDateValue = date
    this.renderCalendar()
    this.renderTimeSlots()
  }

  selectTimeSlot() {
    this.#enableScheduleButtons()
  }

  schedule() {
    const checked = this.timeSlotRadioTargets.find((radio) => radio.checked)
    if (!checked) return

    if (this.hasScheduleFormTarget) {
      const selectedDate = checked.dataset.date || this.selectedDateValue
      if (!selectedDate) return

      this.selectedDateInputTarget.value = selectedDate
      this.selectedTimeInputTarget.value = checked.value

      if (this.hasTimezoneSelectTarget && this.hasSelectedTimeZoneInputTarget) {
        this.selectedTimeZoneInputTarget.value = this.timezoneSelectTarget.value
      }

      this.scheduleFormTarget.requestSubmit()
      return
    }

    this.dispatch("scheduled", {
      detail: { date: this.selectedDateValue, time: checked.value }
    })
  }

  renderCalendar() {
    const year = this.yearValue
    const month = this.monthValue
    const todayDate = this.#todayDateValue()
    const slotsByDate = this.#usesCanonicalSlots() ? this.#slotsByDate() : null

    this.monthLabelTarget.textContent = this.#monthYearLabel(year, month)

    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysInPrevMonth = new Date(year, month, 0).getDate()
    const cells = []

    for (let day = daysInPrevMonth - firstDay + 1; day <= daysInPrevMonth; day++) {
      cells.push(this.#buildOutsideDayCell(year, month - 1, day, slotsByDate, todayDate))
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = this.#formatDateValue(year, month, day)
      const isDisabled = !this.#hasSlotsForDate(dateStr, slotsByDate, todayDate)
      const isSelected = dateStr === this.selectedDateValue
      cells.push(this.#currentDayCell(day, dateStr, isSelected, isDisabled))
    }

    const trailingDays = (7 - ((firstDay + daysInMonth) % 7)) % 7
    for (let day = 1; day <= trailingDays; day++) {
      cells.push(this.#buildOutsideDayCell(year, month + 1, day, slotsByDate, todayDate))
    }

    this.calendarGridTarget.innerHTML = cells.join("")
    this.#renderTimeSlotsPlaceholder()
  }

  renderTimeSlots() {
    if (!this.selectedDateValue) return

    const date = new Date(`${this.selectedDateValue}T12:00:00`)
    this.selectedDateLabelTarget.textContent = this.#selectedDateLabel(date)

    const availableSlots = this.#usesCanonicalSlots()
      ? this.#slotsForDate(this.selectedDateValue)
      : this.#legacySlotsForDate(this.selectedDateValue)
    const takenSlots = this.#usesCanonicalSlots() ? this.#takenSlotsForDate(this.selectedDateValue) : []

    const orderedSlots = [
      ...availableSlots.map((slot) => ({ ...slot, taken: false })),
      ...takenSlots.map((slot) => ({ ...slot, taken: true }))
    ].sort((a, b) => new Date(a.startAt) - new Date(b.startAt))

    if (orderedSlots.length === 0) {
      this.timeSlotsContainerTarget.innerHTML = `<p class="schedule-no-slots">${this.noSlotsTextValue}</p>`
    } else {
      const content = []

      if (availableSlots.length === 0) {
        content.push(`<p class="schedule-no-slots">${this.noSlotsTextValue}</p>`)
      }

      content.push(
        orderedSlots.map((slot) => {
          if (slot.taken) {
            return `<div class="schedule-slot-label schedule-slot-label--taken" aria-disabled="true">
              <span class="schedule-slot-taken-dot" aria-hidden="true"></span>
              <span>${slot.label}</span>
              <span class="schedule-slot-state">${this.takenSlotTextValue}</span>
            </div>`
          }

          const id = `slot-${slot.date}-${slot.timeValue.replace(":", "-")}`
          return `<label class="schedule-slot-label">
            <input type="radio" name="time_slot" value="${slot.timeValue}" id="${id}"
              class="schedule-slot-radio"
              data-date="${slot.date}"
              data-schedule-meeting-target="timeSlotRadio"
              data-action="schedule-meeting#selectTimeSlot">
            ${slot.label}
          </label>`
        }).join("")
      )

      this.timeSlotsContainerTarget.innerHTML = content.join("")
    }

    this.#disableScheduleButtons()
  }

  #slotsForDate(date) {
    return this.#slotsByDate().get(date) || []
  }

  #legacySlotsForDate(date) {
    const entry = this.availableSlotsValue.find((slotEntry) => slotEntry.date === date)
    const labels = entry?.slots || []

    return labels.map((label) => ({
      date,
      label,
      timeValue: label
    }))
  }

  #hasSlotsForDate(date, slotsByDate = null, todayDate = this.#todayDateValue()) {
    if (date < todayDate) return false

    if (this.#usesCanonicalSlots()) {
      return (slotsByDate || this.#slotsByDate()).get(date)?.length > 0
    }

    return this.#legacySlotsForDate(date).length > 0
  }

  #hasAnyAvailableSlots() {
    if (this.hasHasSlotsWithinSixtyDaysValue) {
      return this.hasSlotsWithinSixtyDaysValue
    }

    const todayDate = this.#todayDateValue()

    if (this.#usesCanonicalSlots()) {
      const timeZone = this.#selectedTimeZone()
      return this.#slotStarts().some((startAt) => {
        const slotDate = this.#dateInTimeZone(startAt, timeZone)
        return slotDate >= todayDate
      })
    }

    return this.availableSlotsValue.some((slotEntry) => {
      const hasSlots = (slotEntry.slots || []).length > 0
      return hasSlots && slotEntry.date >= todayDate
    })
  }

  #slotsByDate() {
    return this.#groupedSlotsForStarts(this.#slotStarts())
  }

  #takenSlotsByDate() {
    return this.#groupedSlotsForStarts(this.#takenSlotStarts())
  }

  #groupedSlotsForStarts(slotStarts) {
    const timeZone = this.#selectedTimeZone()
    const grouped = new Map()

    slotStarts.forEach((startAt) => {
      const date = this.#dateInTimeZone(startAt, timeZone)
      const slot = {
        date,
        label: this.#timeLabelInTimeZone(startAt, timeZone),
        timeValue: this.#timeValueInTimeZone(startAt, timeZone),
        startAt
      }

      if (!grouped.has(date)) grouped.set(date, [])
      grouped.get(date).push(slot)
    })

    grouped.forEach((slots, date) => {
      grouped.set(date, slots.sort((a, b) => new Date(a.startAt) - new Date(b.startAt)))
    })

    return grouped
  }

  #takenSlotsForDate(date) {
    return this.#takenSlotsByDate().get(date) || []
  }

  #slotStarts() {
    if (this.#usesCanonicalSlots()) {
      return this.availableSlotStartsValue
    }

    return []
  }

  #takenSlotStarts() {
    if (this.hasTakenSlotStartsValue && this.takenSlotStartsValue.length > 0) {
      return this.takenSlotStartsValue
    }

    return []
  }

  #usesCanonicalSlots() {
    return this.hasAvailableSlotStartsValue || this.hasTakenSlotStartsValue
  }

  #selectedTimeZone() {
    const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

    if (!this.hasTimezoneSelectTarget || !this.timezoneSelectTarget.value) {
      return fallbackTimeZone
    }

    const selected = this.timezoneSelectTarget.value
    const mapped = this.hasTimeZoneMappingsValue ? (this.timeZoneMappingsValue[selected] || selected) : selected

    try {
      new Intl.DateTimeFormat(this.#uiLocale(), { timeZone: mapped }).format(new Date())
      return mapped
    } catch (error) {
      return fallbackTimeZone
    }
  }

  #dateInTimeZone(startAt, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US-u-nu-latn", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(new Date(startAt))

    const year = parts.find((part) => part.type === "year")?.value
    const month = parts.find((part) => part.type === "month")?.value
    const day = parts.find((part) => part.type === "day")?.value

    return `${year}-${month}-${day}`
  }

  #timeLabelInTimeZone(startAt, timeZone) {
    return new Date(startAt).toLocaleTimeString(this.#uiLocale(), {
      timeZone,
      hour: "numeric",
      minute: "2-digit"
    })
  }

  #timeValueInTimeZone(startAt, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US-u-nu-latn", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    }).formatToParts(new Date(startAt))

    let hour = parts.find((part) => part.type === "hour")?.value || "00"
    const minute = parts.find((part) => part.type === "minute")?.value || "00"

    if (hour === "24") hour = "00"

    return `${hour}:${minute}`
  }

  #monthYearLabel(year, month) {
    return new Intl.DateTimeFormat(this.#uiLocale(), {
      month: "long",
      year: "numeric"
    }).format(new Date(year, month, 1))
  }

  #selectedDateLabel(date) {
    return new Intl.DateTimeFormat(this.#uiLocale(), {
      weekday: "long",
      month: "long",
      day: "numeric"
    }).format(date)
  }

  #uiLocale() {
    return document.documentElement.lang || navigator.language || "en"
  }

  #formatDateValue(year, month, day) {
    const m = String(month + 1).padStart(2, "0")
    const d = String(day).padStart(2, "0")
    return `${year}-${m}-${d}`
  }

  #outsideDayCell(day, isDisabled = true) {
    const disabledClass = isDisabled ? " schedule-calendar-day--disabled" : ""
    return `<span class="schedule-calendar-day schedule-calendar-day--outside${disabledClass}">${day}</span>`
  }

  #buildOutsideDayCell(year, month, day, slotsByDate, todayDate) {
    const date = new Date(year, month, day)
    const dateStr = this.#formatDateValue(date.getFullYear(), date.getMonth(), date.getDate())
    const isDisabled = !this.#hasSlotsForDate(dateStr, slotsByDate, todayDate)

    return this.#outsideDayCell(day, isDisabled)
  }

  #todayDateValue() {
    return this.#dateInTimeZone(new Date(), this.#selectedTimeZone())
  }

  #visibleMonthStartDateValue() {
    return this.#formatDateValue(this.yearValue, this.monthValue, 1)
  }

  async #reloadAvailabilityForVisibleMonth() {
    if (!this.hasRefreshUrlValue) return

    const requestId = this.availabilityRequestId + 1
    this.availabilityRequestId = requestId

    const url = new URL(this.refreshUrlValue, window.location.origin)
    if (!url.pathname.endsWith(".json")) {
      url.pathname = `${url.pathname}.json`
    }
    url.searchParams.set("start_date", this.#visibleMonthStartDateValue())
    url.searchParams.set("time_zone", this.#selectedTimeZone())

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok || requestId !== this.availabilityRequestId) return

      const payload = await response.json()
      this.availableSlotStartsValue = Array.isArray(payload.available_slot_starts) ? payload.available_slot_starts : []
      this.takenSlotStartsValue = Array.isArray(payload.taken_slot_starts) ? payload.taken_slot_starts : []
    } catch (error) {
      // Keep existing availability payload when refresh fails.
    }
  }

  #scheduleButtons() {
    const buttons = [this.scheduleButtonTarget]
    if (this.hasMobileScheduleButtonTarget) buttons.push(this.mobileScheduleButtonTarget)
    return buttons
  }

  #enableScheduleButtons() {
    this.#scheduleButtons().forEach((btn) => { btn.disabled = false })
  }

  #disableScheduleButtons() {
    this.#scheduleButtons().forEach((btn) => { btn.disabled = true })
  }

  #renderTimeSlotsPlaceholder() {
    if (this.selectedDateValue || !this.hasTimeSlotsContainerTarget) return

    const hasAnyAvailableSlots = this.#hasAnyAvailableSlots()
    const text = hasAnyAvailableSlots ? this.selectDatePromptText : this.noSlotsTextValue
    const className = hasAnyAvailableSlots ? "tw-text-blue-gray-500 tw-text-text-sm tw-m-0" : "schedule-no-slots"

    this.timeSlotsContainerTarget.innerHTML = `<p class="${className}">${text}</p>`
  }

  #currentDayCell(day, dateStr, isSelected, isDisabled) {
    if (isDisabled) {
      return `<button type="button"
        class="schedule-calendar-day schedule-calendar-day--disabled"
        data-date="${dateStr}"
        disabled
        aria-disabled="true">${day}</button>`
    }

    const stateClass = isSelected ? "schedule-calendar-day--selected" : "schedule-calendar-day--current"

    return `<button type="button"
      class="schedule-calendar-day ${stateClass}"
      data-date="${dateStr}"
      data-action="schedule-meeting#selectDate">${day}<span class="schedule-calendar-day__dot" aria-hidden="true"></span></button>`
  }
}
